import Translations from "@translate/components/Member/Subscription/OnboardingPricingDisclosure";
import { I18nUtil } from "@utils/i18n";
import { agreement } from "@const/agreement";
import { useBridge } from "@/src/lib/bridge/useBridge";
import { MessageType } from "@/src/lib/bridge/types";
import {
  buildConsentCopy,
  buildRefundPolicyCopy,
} from "@utils/member/checkoutConsent";
import { onboardingPricingCopy } from "./onboardingPricingCopy";
import type { OnboardingPricingOffer } from "./onboardingPricingOffers";

const I18n = I18nUtil.createScoped(Translations);

/**
 * web 路径的点击同意正文（EDG-552）：扣款条款 + 不退款条款，与定价页 `CheckoutConsent` 同版本同文本。
 * App 内走 IAP（取消在应用商店，不在 Manage Billing）、终身、条款缺信息时返回 null，沿用原披露文案。
 */
const webConsentText = (
  offer: OnboardingPricingOffer,
  ctaLabel: string | undefined,
  locale: string | undefined,
): string | null => {
  if (!ctaLabel) {
    return null;
  }
  const consent = buildConsentCopy({
    product: offer.product,
    trialProduct: offer.trial ? offer.purchaseProduct : undefined,
    trialDays: offer.trial?.days,
    ctaLabel,
    locale,
  });
  return consent ? `${consent.text} ${buildRefundPolicyCopy(locale)}` : null;
};

export const OnboardingPricingDisclosure = ({
  offer,
  locale,
  showTermsButton,
  consentCtaLabel,
}: {
  offer: OnboardingPricingOffer;
  locale?: string;
  showTermsButton?: boolean;
  /** 触发下单的按钮文案；传入且在 web 时展示点击同意正文（「By clicking "{cta}" …」） */
  consentCtaLabel?: string;
}) => {
  const { send, isInApp } = useBridge();
  const copy = onboardingPricingCopy(offer, locale);
  const consentText = isInApp
    ? null
    : webConsentText(offer, consentCtaLabel, locale);
  const open = (url: string) =>
    isInApp
      ? send(MessageType.OPEN_URL_EXTERNAL, { url })
      : window.open(url, "_blank", "noopener,noreferrer");
  return (
    <>
      {showTermsButton && (
        <button
          type="button"
          className="min-h-11 w-full button_m text-content_primary"
          data-openreplay-label="onboarding_first_terms_button"
          onClick={() => open(agreement.termsLink)}
        >
          {I18n.t("Terms of Use", { locale })}
        </button>
      )}
      <p
        className="body_xs text-center text-content_tertiary"
        data-testid="selected-plan-disclosure"
      >
        {consentText ?? (
          <>
            {copy.disclosure} {copy.consent}
          </>
        )}{" "}
        <button
          type="button"
          className="underline"
          data-openreplay-label="onboarding_first_terms_open"
          onClick={() => open(agreement.termsLink)}
        >
          {I18n.t("Terms of Use", { locale })}
        </button>{" "}
        {I18n.t("and", { locale })}{" "}
        <button
          type="button"
          className="underline"
          data-openreplay-label="onboarding_first_privacy_open"
          onClick={() => open(agreement.privacyLink)}
        >
          {I18n.t("Privacy Policy", { locale })}
        </button>
        .
      </p>
    </>
  );
};
